import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Camera, CheckCircle2, FileSignature, FileText, IdCard, Loader2, MailCheck } from "lucide-react";
import { contractUrl, errText, fetchSignStatus, shrinkImage, signContractLink, uploadCiLink } from "@/lib/bf";
import BFSignSteps, { signSteps } from "./BFSignSteps";
import BFPublicForm from "./BFPublicForm";

const BFCiUpload = ({ t, initial }) => {
  const ref = useRef(null);
  const [n, setN] = useState(initial);
  const [busy, setBusy] = useState(false);
  const pick = (file) => {
    if (!file) return;
    setBusy(true);
    shrinkImage(file, 2000).then((img) => uploadCiLink(t, img)).then(() => setN((x) => x + 1)).catch(() => alert("Nu am putut încărca poza. Încercați din nou."))
      .finally(() => { setBusy(false); ref.current.value = ""; });
  };
  return (
    <div className="bf-sign-firm" data-testid="bf-sign-ci">
      <div className="bf-sign-head"><b><IdCard size={15} /> Buletinul fidejusorului</b>{n > 0 && <span className="bf-sign-st is-semnat" data-testid="bf-sign-ci-count"><CheckCircle2 size={13} /> {n} {n === 1 ? "poză încărcată" : "poze încărcate"}</span>}</div>
      <p className="bf-gate-sub">Fotografiați buletinul fiecărui fidejusor. Poza ajunge la noi odată cu contractul confirmat.</p>
      <input ref={ref} type="file" accept="image/*" capture="environment" hidden onChange={(e) => pick(e.target.files?.[0])} data-testid="bf-sign-ci-input" />
      <button type="button" className="st-btn st-btn--light" onClick={() => ref.current.click()} disabled={busy} data-testid="bf-sign-ci-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} {n ? "Adaugă încă o poză" : "Încarcă poza buletinului"}</button>
    </div>
  );
};

const LABEL = { semnat: "Semnat și trimis", neconfirmat: "Așteaptă confirmarea din e-mail" };

export default function BFContractSign() {
  const { t } = useParams();
  const [x, setX] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState("");
  const [busy, setBusy] = useState(false);
  const reload = () => fetchSignStatus(t).then(setX).catch(() => {});
  useEffect(() => {
    const load = () => fetchSignStatus(t).then(setX).catch(() => setErr("Link invalid sau expirat. Cereți agentului un link nou."));
    load();
  }, [t]);
  const save = (firm, pngs) => {
    setBusy(true); setErr("");
    return signContractLink(t, firm, pngs[0], pngs.slice(1)).then(() => { setX({ ...x, firms: x.firms.map((f) => (f.firm === firm ? { ...f, status: "neconfirmat" } : f)) }); setOpen(""); })
      .catch((e) => { setErr(errText(e, "Nu am putut salva semnătura. Încercați din nou.")); throw e; }).finally(() => setBusy(false));
  };
  return (
    <div className="bf-app bf-gate" data-testid="bf-sign-page">
      <div className="bf-gate-wrap">
        {!x && <div className="bf-gate-card"><p className="bf-gate-sub" data-testid="bf-sign-msg">{err || "Se încarcă…"}</p></div>}
        {x && (
          <div className="bf-gate-card">
            <span className="bf-kicker">Beauty Trends · Contracte</span>
            <h2><FileSignature size={22} /> Semnare contracte {x.firma}</h2>
            <p className="bf-gate-sub">Fiecare contract include, la final, contractul de fidejusiune. Citiți-l, apoi semnați cu degetul (administratorul, apoi fiecare fidejusor). După semnare primiți un e-mail la <b>{x.email}</b>: apăsați „Confirm și trimit contractul” ca să ajungă la noi. Contractul e valabil doar după confirmarea din e-mail.</p>
            {err && <p className="bf-lead-err" data-testid="bf-sign-error">{err}</p>}
            {x.editable && <BFPublicForm t={t} draft={x.draft} onSaved={reload} />}
            {!x.draft && !x.editable && <BFCiUpload t={t} initial={x.ci_count || 0} />}
            {!x.draft && x.firms.map((f) => (
              <div key={f.firm} className="bf-sign-firm" data-testid={`bf-sign-firm-${f.firm}`}>
                <div className="bf-sign-head">
                  <b>{f.name}</b>
                  {f.status && <span className={`bf-sign-st is-${f.status}`} data-testid={`bf-sign-status-${f.firm}`}>{f.status === "semnat" ? <CheckCircle2 size={13} /> : <MailCheck size={13} />} {LABEL[f.status]}</span>}
                </div>
                <div className="share-actions">
                  <a className="st-btn st-btn--light" href={contractUrl(t, f.firm)} target="_blank" rel="noopener noreferrer" data-testid={`bf-sign-read-${f.firm}`}><FileText size={14} /> Citește contractul</a>
                  {f.status !== "semnat" && open !== f.firm && <button type="button" className="st-btn st-btn--silver" onClick={() => setOpen(f.firm)} data-testid={`bf-sign-open-${f.firm}`}><FileSignature size={14} /> {f.status ? "Semnează din nou" : "Semnează"}</button>}
                </div>
                {open === f.firm && <BFSignSteps steps={signSteps(x.admin, [...(x.admin_fid ? [{ admin: true }] : []), ...x.guarantors.map((nume) => ({ nume }))], f.name)} busy={busy} onDone={(p) => save(f.firm, p)} onCancel={() => setOpen("")} testid={`bf-sign-pad-${f.firm}`} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
